// ─────────────────────────────────────────────────────────────────────────────
// TopCities, CD's "Where your audience lives" section (#top-cities-section).
// A cross-platform audience-by-city table (Spotify listeners + IG/FB followers,
// last show, and a Book/✓-booked CTA), from GET /api/booking/top-cities
// (lib fetchTopCities → TopCitiesData). Renders CD's exact .topcities markup;
// the chips reflect the live `sources` map; empty → CD's .tc-empty note.
//
// Mirrors top-cities-wiring.js render/rowHtml (:165-380). Header-click column
// sorting is interactive-only and deferred (the worker default-sorts by total
// audience desc, the column CD shows the ▼ on).
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import type { JSX } from 'react';
import { fetchTopCities } from '../../lib';
import type { TopCity, TopCitiesData } from '../../lib';

export interface TopCitiesProps {
  readonly data: TopCitiesData | null;
  // top-cities-mirror 2026-07-06: the roster Management + Booking views reuse
  // this exact component but are scoped to a viewed artist (X-Band-Id), not the
  // caller's own band. The default Refresh recomputes the CALLER's band, wrong
  // in a roster context, so those views pass a band-scoped refresher here.
  readonly refreshFn?: () => Promise<TopCitiesData | null>;
}

const CHIP_LABELS: Record<string, string> = {
  spotify: 'Spotify',
  ig: 'Instagram',
  /* antenna-cities 2026-07-17 (Ryan): FB replaced, Meta deprecated Page
     city demographics; Antenna followers carry signup city, first-party. */
  antenna: 'Antenna',
  merch: 'Shopify',
  email: 'Database',
};
const CHIP_ORDER = ['spotify', 'ig', 'antenna', 'merch', 'email'];

/** The Antenna tower mark (kit: Antenna/ANTENNA LOGO/antenna-68.svg), sized
 *  for the column header. */
function TowerMark(): JSX.Element {
  return (
    <svg viewBox="0 0 256.58 330.26" width="11" height="14" fill="currentColor" aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 4 }}>
      <path d="M252.43,77.63l-8.45,2.08-50.96,12.91,10.53-8.03-41.25,16.42,21.7-5.27-3.42,5.24,72.43-22.94,3.56-1.18c-1.62-.13-2.84.45-4.15.77Z" />
      <path d="M138.12,65.62l9.94-13.81,3.1,4.63,22.58-37.83,9.57-15.49,1.67-2.94-.27-.17-1.85,2.38-30.93,43.45-.06-10.61-14.25,29.63c-.47.98-1.22,1.75-1.09,2.89l1.58-2.12Z" />
      <path d="M157.84,81.4c-.66.56-1.34.87-1.64,1.66.91-.3,1.38-.73,2.06-1.17l16.75-10.86-.18,5.94,2.64-1.6,46.49-33.39,13.03-9.65c.79-.59,1.65-1.01,2.19-1.77-1.25.17-1.76.86-2.51,1.31l-7.1,4.31-26.69,16.77-20.65,13.11,5.48-9.86-29.87,25.22Z" />
      <path d="M4.15,77.63l8.45,2.08,50.96,12.91-10.53-8.03,41.25,16.42-21.7-5.27,3.42,5.24L3.56,78.04l-3.56-1.18c1.62-.13,2.84.45,4.15.77Z" />
      <path d="M118.45,65.62l-9.94-13.81-3.1,4.63-22.58-37.83-9.57-15.49-1.67-2.94L71.85,0l1.85,2.38,30.93,43.45.06-10.61,14.25,29.63c.47.98,1.22,1.75,1.09,2.89l-1.58-2.12Z" />
      <path d="M98.83,81.4c.66.56,1.34.87,1.64,1.66-.91-.3-1.38-.73-2.06-1.17l-16.75-10.86.18,5.94-2.64-1.6-46.49-33.39-13.03-9.65c-.79-.59-1.65-1.01-2.19-1.77,1.25.17,1.76.86,2.51,1.31l7.1,4.31,26.69,16.77,20.65,13.11-5.48-9.86,29.87,25.22Z" />
      <path d="M187.41,232.7l-37.49-75.56-6.18-1.02c-4.12-4.07-4.12-7.13-4.12-12.22,0-1.02,1.03-3.06,1.03-3.06-.26-.07-2.62-2.14-4.12-3.06l-5.05-31.96c5.85-1.42,10.2-6.62,10.2-12.84,0-7.31-6-13.24-13.39-13.24s-13.39,5.93-13.39,13.24c0,6.22,4.35,11.43,10.2,12.84l-5.05,31.96s-4.14,3.17-4.12,3.06,1.03,2.05,1.03,3.06c0,5.09,0,8.15-4.12,12.22l-6.18,1.02-27.84,48.74-54.76,96.82,23.87,7.19,24.73-7.13,10.3-28.51-14.42-3.06-18.55,34.62-3.09-1.02,19.8-36.63,38.93,8.11-5.15,44.81,27.82,9.17,27.82-9.17-5.15-44.81,36.72-8.2,17.89,36.72-3.09,1.02-16.48-34.62-13.39,3.06,8.24,28.51,18.55,7.13,23.7-7.13c-6.61-11-10.48-21.46-16.08-32.43l-19.58-37.63ZM111.69,210.39l-6.07,48.58-30.61-6.87,41.94-90.9-5.26,49.18ZM128.28,327.21l-4.12-2.04,1.37-83.85.69-82.13-17.39,2.55-9.6,17.54-23.9,42.82-43.89,78.64-2.06-1.02,67.4-120.7,10.77-19.43,20.72-3.37,20.62,2.96s1.37,2.56,1.88,3.03l-20.42-3.03,1.11,70.79.95,95.2-4.12,2.04ZM150.95,258.98l-9.27-97.76,37.98,90.86-28.71,6.9Z" />
    </svg>
  );
}

/** Compact audience number ("9.64k", "1.2m"), mirrors formatCompact. */
function compact(n: number): string {
  if (!n || n < 1000) return String(n || 0);
  if (n < 1_000_000) {
    const v = n / 1000;
    return (v >= 100 ? Math.round(v) : Number(v.toFixed(2))) + 'k';
  }
  return Number((n / 1_000_000).toFixed(2)) + 'm';
}

/** A per-cell count, or CD's em-dash when empty. */
function intFmt(n?: number): string {
  return n != null && n > 0 ? n.toLocaleString('en-US') : '-';
}

/** "Never" / "This mo" / "Xmo ago" / "Xy ago", mirrors relativeFromIso. */
function relShow(iso?: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
  if (isNaN(d.getTime())) return 'Never';
  const now = new Date();
  const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (months <= 0) return 'This mo';
  if (months < 12) return months + 'mo ago';
  return Math.floor(months / 12) + 'y ago';
}

/** "Jun 27" for a booked next-show date. */
function nextShowLabel(iso: string): string {
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function FlagSvg(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M13 2L4.5 13.5H11l-1 8.5L19.5 10H13z" />
    </svg>
  );
}

function ActionCell({ c }: { readonly c: TopCity }): JSX.Element {
  if (c.next_show) {
    return <span className="tc-action tc-action--ok">✓ {nextShowLabel(c.next_show)}</span>;
  }
  if (c.action === 'book') return <span className="tc-action tc-action--book">Book</span>;
  if (c.action === 'route') return <span className="tc-action tc-action--route">Route</span>;
  return <span className="tc-td--muted">-</span>;
}

export function TopCities({ data, refreshFn }: TopCitiesProps): JSX.Element {
  // A manual refresh forces the worker to recompute (refresh=1), busting its 6h
  // cache. The result overrides the prop so a connect/disconnect reflects without
  // a full page reload. null → show the prop data from the dashboard load.
  const [override, setOverride] = useState<TopCitiesData | null>(null);
  const [busy, setBusy] = useState(false);
  const view = override ?? data;
  const cities = view?.cities ?? [];
  const sources = view?.sources ?? {};
  const totalAudience = view?.total_audience ?? 0;
  const marketsTracked = view?.markets_tracked ?? 0;

  const refresh = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      setOverride(await (refreshFn ? refreshFn() : fetchTopCities(true)));
    } catch {
      /* keep showing whatever we have */
    } finally {
      setBusy(false);
    }
  };

  // Live chips first, hide platforms that are 'off' (mirrors renderSources).
  const rank: Record<string, number> = { live: 0, syncing: 1, off: 2 };
  const chips = CHIP_ORDER.map((key) => ({ key, state: sources[key] || 'off' }))
    .filter((c) => c.state !== 'off')
    .sort((a, b) => (rank[a.state] ?? 2) - (rank[b.state] ?? 2));

  return (
    <section className="dash-section" id="top-cities-section">
      <article className="card topcities">
        <header className="tc-head">
          <div className="tc-head__text">
            <p className="eyebrow">Top cities</p>
            <h2 className="tc-title disp">Where your audience lives.</h2>
            <p className="tc-sub">
              Spotify listeners, Instagram followers, Shopify buyers, and your contact database,
              cross-referenced with show history from Bandsintown.
            </p>
          </div>
          <div className="tc-stats">
            <div className="tc-stat">
              <span className="tc-stat__label">Total audience</span>
              <span className="tc-stat__num">{compact(totalAudience)}</span>
            </div>
            <div className="tc-stat">
              <span className="tc-stat__label">Markets tracked</span>
              <span className="tc-stat__num">{marketsTracked}</span>
            </div>
            <button
              className="btn-ghost btn-sm tc-refresh"
              type="button"
              onClick={() => void refresh()}
              disabled={busy}
              title="Recompute from your connected platforms"
            >
              {busy ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </header>

        {chips.length > 0 && (
          <div className="tc-chips">
            {chips.map(({ key, state }) => (
              <span
                key={key}
                className={'tc-chip ' + (state === 'live' ? 'tc-chip--live' : 'tc-chip--sync')}
              >
                {state === 'live' && <span className="tc-dot"></span>}
                {CHIP_LABELS[key]}
                {state === 'syncing' ? ' · syncing' : ''}
              </span>
            ))}
          </div>
        )}

        {cities.length === 0 ? (
          <p className="tc-empty">
            No city data yet, connect Spotify, Instagram, or Shopify and your top markets show up
            here.
          </p>
        ) : (
          <div className="tc-table-wrap">
            <table className="tc-table">
              <thead>
                <tr>
                  <th className="tc-th">City</th>
                  <th className="tc-th tc-th--num tc-th--sorted">Total audience ▼</th>
                  <th className="tc-th tc-th--num">Spotify</th>
                  <th className="tc-th tc-th--num">IG</th>
                  <th className="tc-th tc-th--num"><TowerMark />Antenna</th>
                  <th className="tc-th">Last show</th>
                  <th className="tc-th tc-th--act">Action</th>
                </tr>
              </thead>
              <tbody>
                {cities.map((c, i) => {
                  const isBook = !c.next_show && c.action === 'book';
                  return (
                    <tr className="tc-row" key={c.city + '-' + i}>
                      <td className="tc-td tc-td--city">
                        {isBook ? (
                          <span className="tc-flag">
                            <FlagSvg />
                          </span>
                        ) : (
                          <span className="tc-flag-sp"></span>
                        )}
                        <span>{c.city}</span>
                      </td>
                      <td className="tc-td tc-td--num tc-td--total">{intFmt(c.total)}</td>
                      <td className={'tc-td tc-td--num' + (c.spotify ? '' : ' tc-td--muted')}>
                        {intFmt(c.spotify)}
                      </td>
                      <td className={'tc-td tc-td--num' + (c.ig ? '' : ' tc-td--muted')}>
                        {intFmt(c.ig)}
                      </td>
                      <td className={'tc-td tc-td--num' + (c.antenna ? '' : ' tc-td--muted')}>
                        {intFmt(c.antenna)}
                      </td>
                      <td className="tc-td tc-td--last">{relShow(c.last_show)}</td>
                      <td className="tc-td tc-td--act">
                        <ActionCell c={c} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </article>
    </section>
  );
}
